import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CalculationResults = ({ results, onSaveRoute, onNewCalculation }) => {
  if (!results) return null;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('nb-NO', {
      style: 'currency',
      currency: 'NOK',
      minimumFractionDigits: 2
    })?.format(amount);
  };

  const getDiscountColor = () => {
    if (results?.vehicleType === 'ev') return 'var(--color-success)';
    if (results?.vehicleType === 'hybrid') return 'var(--color-primary)';
    return 'var(--color-muted-foreground)';
  };

  return (
    <div className="w-full space-y-4 md:space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-base md:text-lg font-semibold text-foreground font-heading">
          Beregningsresultat
        </h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={onNewCalculation}
          iconName="X"
          iconPosition="right"
        >
          Ny beregning
        </Button>
      </div>
      <div className="bg-surface-elevated border-2 border-border p-4 md:p-6" style={{ borderRadius: 'var(--radius-md)' }}>
        <div className="space-y-4 md:space-y-6">
          <div className="flex items-center justify-between pb-4 border-b-2 border-border">
            <div>
              <div className="text-xs md:text-sm text-muted-foreground font-caption mb-1">
                Total bomkostnad
              </div>
              <div className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground font-heading">
                {formatCurrency(results?.totalCost)}
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="flex items-center gap-2">
                <Icon name="Route" size={20} color="var(--color-muted-foreground)" />
                <span className="text-sm md:text-base text-muted-foreground font-data">
                  {results?.distance} km
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="Clock" size={20} color="var(--color-muted-foreground)" />
                <span className="text-sm md:text-base text-muted-foreground font-data">
                  {results?.duration}
                </span>
              </div>
            </div>
          </div>

          {results?.discount > 0 && (
            <div
              className="p-4 border-2"
              style={{
                borderRadius: 'var(--radius-sm)',
                background: `${getDiscountColor()}10`,
                borderColor: getDiscountColor()
              }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Icon name="TrendingDown" size={24} color={getDiscountColor()} />
                  <div>
                    <div className="text-sm md:text-base font-medium text-foreground">
                      Rabatt anvendt
                    </div>
                    <div className="text-xs md:text-sm text-muted-foreground font-caption">
                      {results?.vehicleType === 'ev' ? 'Elektrisk kjøretøy' : 'Hybrid kjøretøy'}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-lg md:text-xl font-bold" style={{ color: getDiscountColor() }}>
                    -{formatCurrency(results?.discount)}
                  </div>
                  <div className="text-xs md:text-sm text-muted-foreground">
                    {results?.discountPercentage}% rabatt
                  </div>
                </div>
              </div>
            </div>
          )}

          <div>
            <div className="text-sm md:text-base font-medium text-foreground mb-3 md:mb-4">
              Bomstasjoner på ruten
            </div>
            <div className="space-y-2">
              {results?.tollPoints?.map((point, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 md:p-4 bg-card border-2 border-border"
                  style={{ borderRadius: 'var(--radius-sm)' }}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-8 h-8 flex items-center justify-center text-xs font-medium bg-muted"
                      style={{ borderRadius: 'var(--radius-sm)' }}
                    >
                      {index + 1}
                    </div>
                    <div>
                      <div className="text-sm md:text-base text-foreground font-medium">
                        {point?.name}
                      </div>
                      <div className="text-xs md:text-sm text-muted-foreground font-caption">
                        {point?.location}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm md:text-base font-medium text-foreground font-data">
                      {formatCurrency(point?.cost)}
                    </div>
                    {point?.originalCost > point?.cost && (
                      <div className="text-xs text-muted-foreground line-through">
                        {formatCurrency(point?.originalCost)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="pt-4 border-t-2 border-border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm md:text-base text-muted-foreground">Opprinnelig pris</span>
              <span className="text-sm md:text-base text-muted-foreground font-data">
                {formatCurrency(results?.originalCost)}
              </span>
            </div>
            {results?.discount > 0 && (
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm md:text-base" style={{ color: getDiscountColor() }}>
                  Rabatt ({results?.discountPercentage}%)
                </span>
                <span className="text-sm md:text-base font-data" style={{ color: getDiscountColor() }}>
                  -{formatCurrency(results?.discount)}
                </span>
              </div>
            )}
            <div className="flex items-center justify-between pt-2 border-t-2 border-border">
              <span className="text-base md:text-lg font-semibold text-foreground">Total å betale</span>
              <span className="text-base md:text-lg font-bold text-foreground font-data">
                {formatCurrency(results?.totalCost)}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
        <Button
          variant="outline"
          fullWidth
          onClick={onSaveRoute}
          iconName="Save"
          iconPosition="left"
        >
          Lagre rute
        </Button>
        <Button
          variant="secondary"
          fullWidth
          iconName="Share2"
          iconPosition="left"
        >
          Del beregning
        </Button>
      </div>
    </div>
  );
};

export default CalculationResults;